<template>
  <div class="container-fluid mt-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
      <div>
        <h2 class="fw-bold" style="color: #294b7a;">
          Review Certificate Requests
        </h2>
      </div>

      <div class="d-flex gap-2">
        <button
          class="btn text-white px-4"
          style="background:#b03a45; min-width: 220px; border-radius: 10px;"
          @click="generateCertificate"
        >
          Generate Certificate
        </button>

        <button
          class="btn text-white px-4"
          style="background:#b03a45; min-width: 200px; border-radius: 10px;"
          @click="goToMyCertificates"
        >
          <i class="fas fa-file-alt me-2"></i>
          MY CERTIFICATES
        </button>
      </div>
    </div>

    <!-- Pending Requests Card -->
    <div class="card shadow-sm border-0 rounded-4 overflow-hidden">
      <div
        class="card-header text-white fw-bold py-3"
        style="background: linear-gradient(90deg, #8f2d3a, #50576b);"
      >
        Pending Requests
      </div>

      <div class="card-body p-0">
        <table class="table align-middle mb-0">
          <thead style="background: #f8f9fc;">
            <tr>
              <th class="py-3 px-4">Employee</th>
              <th class="py-3">Certificate Type</th>
              <th class="py-3">Status</th>
              <th class="py-3">Remarks</th>
              <th class="py-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="request in requests" :key="request.id">
              <td class="py-3 px-4">{{ request.employee }}</td>
              <td>{{ request.certificateType }}</td>
              <td>
                <span :class="getStatusClass(request.status)">
                  {{ request.status.toUpperCase() }}
                </span>
              </td>
              <td>{{ request.remarks || "-" }}</td>
              <td class="text-center">
                <template v-if="request.status === 'pending'">
                  <button
                    class="btn btn-success btn-sm me-2 px-3"
                    style="border-radius: 10px;"
                    @click="acceptRequest(request)"
                  >
                    ✔ ACCEPT
                  </button>

                  <button
                    class="btn btn-danger btn-sm px-3"
                    style="border-radius: 10px;"
                    @click="rejectRequest(request)"
                  >
                    ✘ REJECT
                  </button>
                </template>

                <template v-else-if="request.status === 'ready'">
                  <button
                    class="btn btn-sm text-white px-3"
                    style="background:#ff0f6d; border-radius: 10px;"
                    @click="downloadPdf(request)"
                  >
                    <i class="fas fa-file-pdf me-1"></i>
                    PDF
                  </button>
                </template>
              </td>
            </tr>

            <tr v-if="requests.length === 0">
              <td colspan="5" class="text-center text-muted py-4">
                No certificate requests found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import axios from "@/api";
import { useToast } from "vue-toastification";

const router = useRouter();
const toast = useToast();

const requests = ref([]);

const fetchRequests = async () => {
  try {
    const res = await axios.get("/certificate-requests");
    requests.value = res.data?.data?.data || [];
  } catch (error) {
    console.error(error);
    toast.error("Failed to load certificate requests.");
  }
};

const generateCertificate = () => {
  router.push("/employee/certificate-requests/create");
};

const goToMyCertificates = () => {
  router.push("/employee/my-certificates");
};

const acceptRequest = async (request) => {
  try {
    await axios.post(`/certificate-requests/${request.id}/accept`);
    toast.success("Certificate request accepted.");
    fetchRequests();
  } catch (error) {
    console.error(error);
    toast.error("Failed to accept request.");
  }
};

const rejectRequest = async (request) => {
  try {
    await axios.post(`/certificate-requests/${request.id}/reject`);
    toast.success("Certificate request rejected.");
    fetchRequests();
  } catch (error) {
    console.error(error);
    toast.error("Failed to reject request.");
  }
};

const downloadPdf = async (request) => {
  try {
    const response = await axios.get(`/certificate-requests/${request.id}/pdf`, {
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `certificate_${request.id}.pdf`;
    link.click();

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
    toast.error("Failed to download PDF.");
  }
};

const getStatusClass = (status) => {
  if (status === "pending") {
    return "badge rounded-pill text-bg-secondary px-3 py-2";
  }

  if (status === "ready") {
    return "badge rounded-pill text-bg-success px-3 py-2";
  }

  if (status === "rejected") {
    return "badge rounded-pill text-bg-danger px-3 py-2";
  }

  return "badge rounded-pill text-bg-light text-dark px-3 py-2";
};

onMounted(() => {
  fetchRequests();
});
</script>

<style scoped>
.table th,
.table td {
  vertical-align: middle;
}

.table tbody tr {
  border-top: 1px solid #e56b78;
}

.card {
  background: #fff;
}
</style>